import { z } from 'zod';

export const REMINDER_DAYS_GROUP_VALIDATION_KEY = 'reminderDays';

const cancellationRuleSchema = z.object({
  days: z.number().nullable().optional(),
  type: z.string().optional(),
  value: z.number().nullable().optional(),
}).passthrough();

const rushPremiumTierSchema = z.object({
  days: z.number().nullable().optional(),
  type: z.string().optional(),
  value: z.number().nullable().optional(),
}).passthrough();

export const invoiceSchema = z
  .object({
    paymentTermsDays: z.union([z.number(), z.string()]).nullable().optional(),
    reminderDays: z.array(z.number()).nullable().optional(),
    autoSendReminders: z.boolean().nullable().optional(),
    useLateFee: z.boolean().nullable().optional(),
    lateFeeType: z.string().nullable().optional(),
    lateFeePercentage: z.number().nullable().optional(),
    lateFeeFixedAmount: z.number().nullable().optional(),
    recurringLateFee: z.string().nullable().optional(),
    recurringLateFeeInterval: z.number().nullable().optional(),
    useEarlyPaymentDiscount: z.boolean().nullable().optional(),
    earlyPaymentType: z.string().nullable().optional(),
    earlyPaymentDiscountPercentage: z.number().nullable().optional(),
    earlyPaymentFixedAmount: z.number().nullable().optional(),
    earlyPaymentDays: z.number().nullable().optional(),
    useCancellationPolicy: z.boolean().nullable().optional(),
    cancellationPolicy: z.array(cancellationRuleSchema).nullable().optional(),
    rushPremiumPolicy: z.array(rushPremiumTierSchema).nullable().optional(),
  })
  .passthrough()
  .superRefine((data, ctx) => {
    const paymentTerms = data.paymentTermsDays == null ? null : Number(data.paymentTermsDays);

    if (!data.paymentTermsDays || paymentTerms === null || isNaN(paymentTerms) || paymentTerms < 1 || paymentTerms > 120) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['paymentTermsDays'],
        message: 'Payment terms must be between 1 and 120 days',
      });
    }

    // Validate reminder days
    if (data.reminderDays && data.reminderDays.length > 0) {
      if (paymentTerms != null && paymentTerms > 0) {
        const reminderDaysOverLimit = data.reminderDays.some(
          (day) => day > 0 && Number(day) > paymentTerms
        );
        if (reminderDaysOverLimit) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['reminderDays'],
            message: `Reminder days must be ≤ payment terms (${data.paymentTermsDays})`,
          });
        }
      }

      if (data.autoSendReminders) {
        const hasValidReminder = data.reminderDays.some((day) => day > 0);
        if (!hasValidReminder) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['reminderDays'],
            message: 'At least one reminder day is required when auto-send is enabled',
          });
        }
      }
    } else if (data.autoSendReminders) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reminderDays'],
        message: 'At least one reminder day is required when auto-send is enabled',
      });
    }

    // Late fee validation
    if (data.useLateFee) {
      if (data.lateFeeType === 'percentage') {
        if (data.lateFeePercentage == null || data.lateFeePercentage < 0.01 || data.lateFeePercentage > 100) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['lateFeePercentage'],
            message: 'Late fee percentage must be between 0.01 and 100',
          });
        }
      } else {
        if (data.lateFeeFixedAmount == null || data.lateFeeFixedAmount <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['lateFeeFixedAmount'],
            message: 'Late fee amount must be greater than 0',
          });
        }
      }
      if (data.recurringLateFee === 'interval') {
        if (data.recurringLateFeeInterval == null || data.recurringLateFeeInterval < 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['recurringLateFeeInterval'],
            message: 'Recurring late fee interval must be at least 1 day',
          });
        }
      }
    }

    // Early payment discount validation
    if (data.useEarlyPaymentDiscount) {
      if (data.earlyPaymentType === 'percentage') {
        if (
          data.earlyPaymentDiscountPercentage == null ||
          data.earlyPaymentDiscountPercentage < 0.01 ||
          data.earlyPaymentDiscountPercentage > 100
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['earlyPaymentDiscountPercentage'],
            message: 'Discount % must be 0.01-100',
          });
        }
        if (data.earlyPaymentFixedAmount !== null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['earlyPaymentFixedAmount'],
            message: 'Fixed amount must be null when type is %',
          });
        }
      } else {
        if (data.earlyPaymentFixedAmount == null || data.earlyPaymentFixedAmount <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['earlyPaymentFixedAmount'],
            message: 'Fixed amount must be > 0',
          });
        }
        if (data.earlyPaymentDiscountPercentage !== null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['earlyPaymentDiscountPercentage'],
            message: 'Discount % must be null when type is $',
          });
        }
      }

      if (
        data.earlyPaymentDays === null ||
        Number(data.earlyPaymentDays) <= 0 ||
        (paymentTerms != null && Number(data.earlyPaymentDays) >= paymentTerms)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['earlyPaymentDays'],
          message: 'Early-payment days must be < payment terms',
        });
      }
    }

    // Cancellation policy validation
    if (data.useCancellationPolicy) {
      if (!data.cancellationPolicy || data.cancellationPolicy.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['cancellationPolicy'],
          message: 'At least one cancellation rule is required when cancellation policy is enabled',
        });
      } else {
        data.cancellationPolicy.forEach((rule, index) => {
          if (rule.days === null || rule.days === undefined || rule.days < 1) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [`cancellationPolicy.${index}.days`],
              message: 'Cancellation policy days must be at least 1',
            });
          }

          if (rule.type === 'percentage') {
            if (rule.value === null || rule.value === undefined || rule.value < 0.01 || rule.value > 100) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [`cancellationPolicy.${index}.value`],
                message: 'Cancellation surcharge percentage must be between 0.01 and 100',
              });
            }
          } else {
            if (rule.value === null || rule.value === undefined || rule.value <= 0) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [`cancellationPolicy.${index}.value`],
                message: 'Cancellation surcharge amount must be greater than 0',
              });
            }
          }
        });
      }
    }

    // Rush premium policy validation
    if (data.rushPremiumPolicy && data.rushPremiumPolicy.length > 0) {
      const daysSeen = new Set<number>();
      data.rushPremiumPolicy.forEach((tier, index) => {
        if (tier.days === null || tier.days === undefined || tier.days < 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [`rushPremiumPolicy.${index}.days`],
            message: 'Lead time threshold must be at least 1 day',
          });
        } else if (daysSeen.has(tier.days)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [`rushPremiumPolicy.${index}.days`],
            message: 'Duplicate lead time threshold',
          });
        } else {
          daysSeen.add(tier.days);
        }

        if (tier.type === 'percentage') {
          if (tier.value === null || tier.value === undefined || tier.value < 0.01) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [`rushPremiumPolicy.${index}.value`],
              message: 'Rush premium percentage must be at least 0.01%',
            });
          }
        } else {
          if (tier.value === null || tier.value === undefined || tier.value <= 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [`rushPremiumPolicy.${index}.value`],
              message: 'Rush premium amount must be greater than 0',
            });
          }
        }
      });
    }
  });

export type InvoiceSchemaData = z.infer<typeof invoiceSchema>;
